/**
 * ClickPreviewCard — floating live preview anchored beside the clicked hex (ss3).
 * Screen-space (not scaled by the canvas transform). Sits right of the hex, flips
 * left near the edge, notch points back at the hex. The video player is INJECTED
 * (local ULLFUPlayer / cloud LiveStreamPlayer) via PlayerComponent.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  FilmIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';
import type { HexPlayerComponent } from './types';
import type { HexCounter } from '../../api/contracts';

const CARD_W = 268;
const CARD_H = 196;
const MARGIN = 8;

const SEV_COUNTS: { key: keyof HexCounter; label: string; dot: string }[] = [
  { key: 'critical', label: 'Critical', dot: 'bg-red-500' },
  { key: 'warning', label: 'Warning', dot: 'bg-amber-400' },
  { key: 'normal', label: 'Info', dot: 'bg-blue-500' },
];

function StatusPill({ label, on }: { label: string; on: boolean }) {
  return (
    <span className={`flex items-center gap-1 text-[10px] font-medium ${on ? 'text-emerald-500' : 'text-muted-foreground'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-emerald-400' : 'bg-slate-400/60'}`} />
      {label}
    </span>
  );
}

interface ClickPreviewCardProps {
  cameraId: string;
  name: string;
  caption?: string;
  live: boolean;
  recording: boolean;
  counter?: HexCounter;
  anchorX: number;
  anchorY: number;
  hexHalfW: number;
  viewportW: number;
  viewportH: number;
  PlayerComponent: HexPlayerComponent;
  onClose: () => void;
  /**
   * Jump to this camera's recorded footage. Injected rather than routed here — the shared
   * package doesn't own either app's routes — and omitted entirely when the caller lacks
   * `recordings.view`, which is what hides the button.
   */
  onOpenRecordings?: (cameraId: string) => void;
}

export default function ClickPreviewCard({
  cameraId,
  name,
  caption,
  live,
  recording,
  counter,
  anchorX,
  anchorY,
  hexHalfW,
  viewportW,
  viewportH,
  PlayerComponent,
  onClose,
  onOpenRecordings,
}: ClickPreviewCardProps) {
  // Fullscreen the VIDEO, not the card: the operator wants the picture bigger, not the
  // alert counters. Held on the wrapper so the player keeps its own DOM and the WebRTC
  // subscription survives — remounting it would tear the stream down and re-handshake.
  const videoRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === videoRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      // Closing the preview (or clicking another hex) while fullscreen would otherwise strand
      // the browser showing a detached element.
      if (document.fullscreenElement === videoRef.current) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen().catch(() => {});
  }, []);
  let left = anchorX + hexHalfW + 12;
  let side: 'left' | 'right' = 'left';
  if (left + CARD_W > viewportW - MARGIN) {
    left = anchorX - hexHalfW - 12 - CARD_W;
    side = 'right';
  }
  left = Math.max(MARGIN, Math.min(left, viewportW - CARD_W - MARGIN));

  let top = anchorY - CARD_H / 2;
  top = Math.max(MARGIN, Math.min(top, viewportH - CARD_H - MARGIN));
  const notchTop = Math.max(14, Math.min(anchorY - top, CARD_H - 22));

  return (
    <div
      className="pointer-events-auto absolute z-30 animate-modal-pop overflow-hidden rounded-lg border border-border bg-card shadow-modal"
      style={{ left, top, width: CARD_W }}
    >
      <div
        className="absolute h-3 w-3 rotate-45 border border-border bg-card"
        style={{
          top: notchTop,
          [side === 'left' ? 'left' : 'right']: -6,
          borderRight: side === 'left' ? undefined : 'none',
          borderTop: side === 'left' ? undefined : 'none',
          borderLeft: side === 'left' ? 'none' : undefined,
          borderBottom: side === 'left' ? 'none' : undefined,
        }}
      />
      {/* `group` so the action bar reveals on hover, the same way the camera grid's tiles
          behave — the preview is small and the picture is what the operator opened it for. */}
      <div ref={videoRef} className="group relative bg-black">
        <PlayerComponent
          cameraId={cameraId}
          label={name}
          className={isFullscreen ? 'h-full w-full' : 'aspect-video w-full'}
        />
        <button
          type="button"
          onClick={onClose}
          className="absolute right-1.5 top-1.5 z-10 rounded bg-black/50 p-1 text-white hover:bg-black/70"
          aria-label="Close preview"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>

        {/* Same controls, same place, same styling as a camera tile's hover actions. */}
        <div className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {onOpenRecordings && (
            <Tooltip label="Recording playback — review this camera's past footage" side="top">
              <button
                type="button"
                onClick={() => onOpenRecordings(cameraId)}
                className="rounded-lg bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
                aria-label="Recording playback"
              >
                <FilmIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          <Tooltip label={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'} side="top">
            <button
              type="button"
              onClick={toggleFullscreen}
              className="rounded-lg bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="space-y-1.5 border-t border-border px-2.5 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-foreground">{name}</span>
          {caption && <span className="truncate text-[10px] text-muted-foreground">{caption}</span>}
        </div>
        <div className="flex items-center gap-3">
          <StatusPill label="Live" on={live} />
          <StatusPill label="Rec" on={recording} />
        </div>
        <div className="flex items-center gap-2.5">
          {SEV_COUNTS.map((sv) => (
            <span key={sv.key} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className={`h-1.5 w-1.5 rounded-full ${sv.dot}`} />
              <span className="font-semibold tabular-nums text-foreground">{counter?.[sv.key] ?? 0}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
